import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/esm/Check';

interface WorkerTaskCompleteOverlayProps {
  open: boolean;
  busy?: boolean;
  durationLabel?: string;
  onReturn: () => void;
  onUndo: () => void | Promise<void>;
}

export default function WorkerTaskCompleteOverlay({
  open,
  busy = false,
  durationLabel,
  onReturn,
  onUndo,
}: WorkerTaskCompleteOverlayProps) {
  return (
    <Dialog
      fullScreen
      open={open}
      PaperProps={{
        sx: {
          bgcolor: '#111',
          color: '#fff',
          background: 'radial-gradient(circle at 50% 18%, rgba(255,255,255,0.09), transparent 30%), #111',
        },
      }}
    >
      <Stack
        spacing={3}
        alignItems="center"
        justifyContent="space-between"
        sx={{ minHeight: '100dvh', px: { xs: 2, sm: 4 }, py: { xs: 5, sm: 7 }, textAlign: 'center' }}
      >
        <Box />

        <Stack spacing={2.25} alignItems="center" sx={{ width: '100%', maxWidth: 440 }}>
          <Box
            sx={{
              width: { xs: 112, sm: 132 },
              height: { xs: 112, sm: 132 },
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: '#1b211c',
              background: `
                radial-gradient(circle at 35% 28%, rgba(255,255,255,0.95), rgba(244,247,240,0.82) 44%, rgba(202,214,200,0.78) 100%)
              `,
              boxShadow: '0 22px 60px rgba(0,0,0,0.34), inset 0 2px 8px rgba(255,255,255,0.85), inset 0 -8px 18px rgba(90,110,92,0.22)',
            }}
          >
            <Box
              sx={{
                width: { xs: 62, sm: 72 },
                height: { xs: 62, sm: 72 },
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                transform: 'rotate(-8deg)',
                bgcolor: 'rgba(255,255,255,0.42)',
                boxShadow: 'inset 0 1px 6px rgba(255,255,255,0.9)',
              }}
            >
              <CheckIcon sx={{ fontSize: { xs: 44, sm: 52 }, strokeWidth: 2.4 }} />
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontSize: { xs: 28, sm: 34 }, fontWeight: 900, lineHeight: 1.05 }}>
              Task Completed
            </Typography>
            <Typography sx={{ mt: 1.2, color: 'rgba(255,255,255,0.68)', fontSize: { xs: 15, sm: 17 }, lineHeight: 1.45 }}>
              Your task has been sent to the supervisor for inspection
            </Typography>
            {durationLabel && (
              <Typography sx={{ mt: 1.5, color: 'rgba(255,255,255,0.9)', fontSize: { xs: 17, sm: 19 }, fontWeight: 900 }}>
                Time taken: {durationLabel}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: '100%', maxWidth: 440 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={onReturn}
            sx={{
              minHeight: 54,
              borderRadius: 2,
              bgcolor: '#6cf083',
              color: '#101510',
              fontWeight: 900,
              '&:hover': { bgcolor: '#58db70' },
            }}
          >
            Return to Field Mode
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={busy}
            onClick={() => void onUndo()}
            sx={{
              minHeight: 54,
              borderRadius: 2,
              bgcolor: '#fff',
              color: '#151515',
              fontWeight: 900,
              '&:hover': { bgcolor: '#f1f1f1' },
            }}
          >
            Undo
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
